import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { uploadConnectorService, ProcessingStatus } from '../services/uploadConnector';

interface ActiveProcessing {
  id: string;
  batchId: string;
  taskId: string;
  imageCount: number;
  status: ProcessingStatus | null;
  startTime: Date;
}

interface ProcessingContextType {
  activeProcessing: ActiveProcessing | null;
  isProcessing: boolean;
  showToast: boolean;
  startProcessing: (batchId: string, taskId: string, imageCount: number) => void;
  dismissToast: () => void;
  clearProcessing: () => void;
}

const ProcessingContext = createContext<ProcessingContextType | null>(null);

export function ProcessingProvider({ children }: { children: React.ReactNode }) {
  const [activeProcessing, setActiveProcessing] = useState<ActiveProcessing | null>(null);
  const [showToast, setShowToast] = useState(false);
  const processingIdRef = useRef(0);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const pollStatus = useCallback(async (taskId: string, processingId: string) => {
    try {
      const result = await uploadConnectorService.getProcessingStatus(taskId);

      setActiveProcessing(prev => {
        if (prev?.id !== processingId) return prev;
        return { ...prev, status: result };
      });

      // Stop polling if complete or failed
      if (result.status === 'SUCCESS' || result.status === 'FAILURE') {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }

        // Auto-hide toast after completion (keep for 5 seconds)
        setTimeout(() => {
          setActiveProcessing(prev => {
            if (prev?.id !== processingId) return prev;
            return null;
          });
          setShowToast(false);
        }, 5000);
      }
    } catch (err) {
      console.error('Failed to poll processing status:', err);
    }
  }, []);

  const startProcessing = useCallback((batchId: string, taskId: string, imageCount: number) => {
    const processingId = `processing-${++processingIdRef.current}`;

    // Clear any existing polling
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    setActiveProcessing({
      id: processingId,
      batchId,
      taskId,
      imageCount,
      status: null,
      startTime: new Date(),
    });
    setShowToast(true);

    // Start polling immediately
    pollStatus(taskId, processingId);

    // Then poll every 2 seconds
    pollIntervalRef.current = setInterval(() => {
      pollStatus(taskId, processingId);
    }, 2000);
  }, [pollStatus]);

  const dismissToast = useCallback(() => {
    setShowToast(false);
    // Note: Processing continues in background, we just hide the toast
  }, []);

  const clearProcessing = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    setActiveProcessing(null);
    setShowToast(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  const isProcessing = activeProcessing !== null &&
    activeProcessing.status?.status !== 'SUCCESS' &&
    activeProcessing.status?.status !== 'FAILURE';

  return (
    <ProcessingContext.Provider value={{
      activeProcessing,
      isProcessing,
      showToast,
      startProcessing,
      dismissToast,
      clearProcessing,
    }}>
      {children}
    </ProcessingContext.Provider>
  );
}

export function useProcessing() {
  const context = useContext(ProcessingContext);
  if (!context) {
    throw new Error('useProcessing must be used within a ProcessingProvider');
  }
  return context;
}
